/*
 * video-facade — privacy-first click-to-load video facade.
 * --------------------------------------------------------------------------
 * Compliance contract (HarvestRight Cookie Compliance plan, Rev. 2.1, Task 3):
 *  - NO third-party iframe (YouTube/Vimeo/TikTok) is in the initial DOM. The
 *    real embed URL lives in a data-attribute and is only turned into an
 *    <iframe> on user click OR when consent is granted.
 *  - The poster is a self-hosted Shopify-CDN image (rendered by Liquid). This
 *    script never fetches img.youtube.com / i.ytimg.com.
 *  - Liquid pre-builds the embed URL on youtube-nocookie.com / Vimeo dnt=1.
 *  - Re-render on consent WITHOUT reload: when the visitor accepts, any facade
 *    marked data-consent-autoplay swaps in its iframe immediately.
 *
 * Markup (rendered by snippets/video-facade.liquid):
 *   <video-facade
 *       data-embed-url="https://www.youtube-nocookie.com/embed/ID?..."
 *       data-autoplay="true"            // append autoplay params on inject
 *       data-consent-autoplay="true">   // auto-inject once marketing consent granted
 *     <img class="video-facade__poster" ...>
 *     <button class="video-facade__play" type="button" aria-label="Play video">...</button>
 *   </video-facade>
 */
if (!customElements.get('video-facade')) {
  customElements.define(
    'video-facade',
    class VideoFacade extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;

        this.button = this.querySelector('.video-facade__play') || this.querySelector('button');
        if (this.button) {
          this.button.addEventListener('click', () => this.loadEmbed());
        }
        // Keyboard activation falls through to the native <button> click.

        // If the visitor already granted consent (returning visitor), and this
        // facade is allowed to auto-render, do it now.
        if (this.consentAutoplay && this.hasMarketingConsent()) {
          this.loadEmbed();
        }

        // Re-render on consent change without a page reload.
        if (this.consentAutoplay) {
          this._onConsent = () => {
            if (this.hasMarketingConsent()) {
              this.loadEmbed();
            }
          };
          document.addEventListener('visitorConsentCollected', this._onConsent);
        }
      }

      disconnectedCallback() {
        if (this._onConsent) {
          document.removeEventListener('visitorConsentCollected', this._onConsent);
        }
      }

      get consentAutoplay() {
        return this.getAttribute('data-consent-autoplay') === 'true';
      }

      get autoplay() {
        return this.getAttribute('data-autoplay') === 'true';
      }

      hasMarketingConsent() {
        try {
          var cp = window.Shopify && window.Shopify.customerPrivacy;
          if (cp && typeof cp.currentVisitorConsent === 'function') {
            var c = cp.currentVisitorConsent();
            return c && (c.marketing === 'yes' || c.marketing === true);
          }
        } catch (e) { /* fail closed */ }
        return false;
      }

      loadEmbed() {
        if (this.loaded) return;
        var base = this.getAttribute('data-embed-url');
        if (!base) return;
        this.loaded = true;

        var src = base;
        if (this.autoplay) {
          src += (src.indexOf('?') === -1 ? '?' : '&') + 'autoplay=1&mute=1';
        }

        var iframe = document.createElement('iframe');
        iframe.setAttribute('src', src);
        iframe.setAttribute('loading', 'lazy');
        iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
        iframe.setAttribute('allowfullscreen', 'allowfullscreen');
        iframe.setAttribute('title', this.getAttribute('data-title') || 'Video');
        iframe.className = 'video-facade__iframe';

        var poster = this.querySelector('.video-facade__poster');
        if (poster) poster.style.display = 'none';
        if (this.button) this.button.style.display = 'none';
        this.appendChild(iframe);
      }
    }
  );
}
